/**
 * GRUNNOPPSETT OG RESSURSER PÅ SPILLESTEDET (sak 229.1–229.2, agent V2, migrering 0447).
 *
 * «En ressurs kan være alt — et sted, en ting, noe som brukes og kan reserveres, fra en bar til en
 * mikrofon.» Tre ting, alle husets egne rader:
 *
 *   `VenueBaseResource`   salens grunnoppsett: ressurs + antall (`/venue-rooms/:id/base-resources`)
 *   `VenueNightResources` arrangementets reservasjoner og konfliktene deres
 *                         (`/venue-nights/:id/resources`)
 *   `VenueResource`       husets ressursregister uten årgang (`/venue-resources?festivalId=`)
 *
 * INGEN ÅRGANG I NOEN AV SVARENE. Reservasjonen hører til husets skjulte årgang internt; skjermen
 * vet ikke at den finnes (INSTRUKS: «Ingen årgang synlig»).
 */
import { z } from 'zod';

import { cursorQuerySchema, page } from '../http/pagination.js';
import { capacityModeSchema } from './resources.js';

const instant = z.iso.datetime({ offset: true });
const quantity = z.number().int().min(1).max(100_000);
const note = z.string().trim().max(500);

/** En av husets ressurser i en velger (grunnoppsettet og «Legg til ressurs»). */
export const venueResourceOptionSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    typeName: z.string(),
    capacityMode: capacityModeSchema,
    /** Hvor mange som finnes, for en pool eller delt ressurs. */
    capacity: z.number().int().nullable(),
  })
  .meta({ id: 'VenueResourceOption' });
export type VenueResourceOption = z.infer<typeof venueResourceOptionSchema>;

/* ------------------------------------------------------------------------------ grunnoppsett */

export const venueBaseResourceSchema = z
  .object({
    id: z.uuid(),
    venueRoomId: z.uuid(),
    resourceId: z.uuid(),
    resourceName: z.string(),
    resourceTypeName: z.string(),
    capacityMode: capacityModeSchema,
    /** Null = én (eller «vi teller ikke» på en eksklusiv ressurs). */
    quantity: z.number().int().nullable(),
    note: z.string().nullable(),
    sortOrder: z.number().int(),
  })
  .meta({ id: 'VenueBaseResource' });
export type VenueBaseResource = z.infer<typeof venueBaseResourceSchema>;

export const venueBaseResourceListSchema = z
  .object({
    items: z.array(venueBaseResourceSchema),
    /** Husets ressurser, til «Legg til i grunnoppsettet». */
    available: z.array(venueResourceOptionSchema),
    /** 311.4: huset har flere ressurser enn velgerens tak (2 000), og resten står ikke i `available`. */
    availableTruncated: z.boolean().optional(),
  })
  .meta({ id: 'VenueBaseResourceList' });
export type VenueBaseResourceList = z.infer<typeof venueBaseResourceListSchema>;

export const createVenueBaseResourceRequestSchema = z
  .object({
    resourceId: z.uuid(),
    quantity: quantity.nullish(),
    note: note.nullish(),
  })
  .meta({ id: 'CreateVenueBaseResourceRequest' });
export type CreateVenueBaseResourceRequest = z.infer<typeof createVenueBaseResourceRequestSchema>;

export const updateVenueBaseResourceRequestSchema = z
  .object({
    quantity: quantity.nullish(),
    note: note.nullish(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .meta({ id: 'UpdateVenueBaseResourceRequest' });
export type UpdateVenueBaseResourceRequest = z.infer<typeof updateVenueBaseResourceRequestSchema>;

/* ------------------------------------------------------------------ arrangementets reservasjoner */

export const VENUE_RESOURCE_CONFLICT_RULES = ['exclusive', 'capacity'] as const;

/**
 * Den ANDRE reservasjonen som vil ha den samme ressursen samtidig. `nightId` er satt når den hører
 * til et annet arrangement i huset (også i en annen sal og over nyttår); ellers er det husets egen
 * konsert eller en reservasjon noen har lagt for hånd, og `title` er det den heter.
 */
export const venueResourceConflictSchema = z
  .object({
    allocationId: z.uuid(),
    title: z.string(),
    nightId: z.uuid().nullable(),
    roomName: z.string().nullable(),
    startsAt: instant,
    endsAt: instant,
    rule: z.enum(VENUE_RESOURCE_CONFLICT_RULES),
    /** `capacity`: hvor mange som er i bruk samtidig, med denne, og hvor mange som finnes. */
    used: z.number().int().nullable(),
    capacity: z.number().int().nullable(),
  })
  .meta({ id: 'VenueResourceConflict' });
export type VenueResourceConflict = z.infer<typeof venueResourceConflictSchema>;

export const venueNightReservationSchema = z
  .object({
    id: z.uuid(),
    resourceId: z.uuid(),
    resourceName: z.string(),
    resourceTypeName: z.string(),
    capacityMode: capacityModeSchema,
    quantity: z.number().int().nullable(),
    note: z.string().nullable(),
    /** `base` kom fra salens grunnoppsett; ellers lagt til på arrangementet. */
    fromBase: z.boolean(),
    cancelled: z.boolean(),
    startsAt: instant,
    endsAt: instant,
    conflicts: z.array(venueResourceConflictSchema),
  })
  .meta({ id: 'VenueNightReservation' });
export type VenueNightReservation = z.infer<typeof venueNightReservationSchema>;

export const venueNightResourcesSchema = z
  .object({
    nightId: z.uuid(),
    /** Arrangementets tidsrom: get-in (ellers dører − 2 t) til curfew (ellers dører + 5 t). */
    startsAt: instant,
    endsAt: instant,
    items: z.array(venueNightReservationSchema),
    /** Hvor mange rader i salens grunnoppsett arrangementet ikke har — «Hent grunnoppsett». */
    baseMissing: z.number().int(),
    /** Husets ressurser, til «Legg til ressurs». */
    available: z.array(venueResourceOptionSchema),
    /** 311.4: huset har flere ressurser enn velgerens tak (2 000), og resten står ikke i `available`. */
    availableTruncated: z.boolean().optional(),
  })
  .meta({ id: 'VenueNightResources' });
export type VenueNightResources = z.infer<typeof venueNightResourcesSchema>;

export const addVenueNightResourceRequestSchema = z
  .object({
    resourceId: z.uuid(),
    quantity: quantity.nullish(),
    note: note.nullish(),
  })
  .meta({ id: 'AddVenueNightResourceRequest' });
export type AddVenueNightResourceRequest = z.infer<typeof addVenueNightResourceRequestSchema>;

/* ------------------------------------------------------------------------- husets register */

export const VENUE_RESOURCE_SORT_KEYS = ['name'] as const;

export const listVenueResourcesQuerySchema = cursorQuerySchema(VENUE_RESOURCE_SORT_KEYS)
  .extend({
    festivalId: z.uuid(),
    q: z.string().trim().min(1).max(100).optional(),
    resourceTypeId: z.uuid().optional(),
    /** B9: gruppen, med alle undergruppene under den. */
    groupId: z.uuid().optional(),
    includeArchived: z.stringbool().default(false),
  })
  .meta({ id: 'ListVenueResourcesQuery' });
export type ListVenueResourcesQuery = z.infer<typeof listVenueResourcesQuerySchema>;

/** En egenskap på en ressurs: et felt typen har (`fields`) og verdien ressursen har i det. */
export const venueResourcePropertySchema = z.object({
  key: z.string(),
  label: z.string(),
  value: z.string(),
});
export type VenueResourceProperty = z.infer<typeof venueResourcePropertySchema>;

const propertyInput = z.object({
  label: z.string().trim().min(1).max(60),
  value: z.string().trim().max(500),
});

/**
 * «HVA ER DET?» NÅR TYPEN IKKE FINNES (B9, Del 0 #15). Typen er påkrevd i datamodellen, men lages i samme
 * kall som ressursen, så ingen må lage en type først. `presetKey` er et av plattformens forslag.
 */
export const newVenueResourceTypeSchema = z.object({
  name: z.string().trim().min(1).max(80),
  capacityMode: z.enum(['exclusive', 'pool']),
  presetKey: z.string().trim().min(1).max(64).optional(),
});
export type NewVenueResourceType = z.infer<typeof newVenueResourceTypeSchema>;

export const venueResourceRoomSchema = z.object({
  roomId: z.uuid(),
  roomName: z.string(),
  quantity: z.number().int().nullable(),
});

export const venueResourceSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    subtitle: z.string().nullable(),
    notes: z.string().nullable(),
    resourceTypeId: z.uuid(),
    resourceTypeName: z.string(),
    capacityMode: capacityModeSchema,
    /** Hvor mange som finnes (pool/delt): ressursens egen, ellers typens. */
    capacity: z.number().int().nullable(),
    capacityOverride: z.number().int().nullable(),
    unit: z.string().nullable(),
    archived: z.boolean(),
    /** Salene som har den i grunnoppsettet. */
    rooms: z.array(venueResourceRoomSchema),
    /** B9: gruppen ressursen står i, og veien til den («Lyd › Mikrofoner»). */
    groupId: z.uuid().nullable().optional(),
    groupPath: z.array(z.string()).optional(),
    /** B9: egenskapene brukeren har lagt på (modell, serienummer), som typens egne felt med verdi. */
    properties: z.array(venueResourcePropertySchema).optional(),
  })
  .meta({ id: 'VenueResource' });
export type VenueResource = z.infer<typeof venueResourceSchema>;

export const venueResourceListResponseSchema = page(
  venueResourceSchema,
  'VenueResourceListResponse',
);
export type VenueResourceListResponse = z.infer<typeof venueResourceListResponseSchema>;

/** Endring, sletting og «Angre» sier hvilket hus de skjer i; årgangen finner serveren selv. */
export const venueResourceScopeQuerySchema = z
  .object({ festivalId: z.uuid() })
  .meta({ id: 'VenueResourceScopeQuery' });
export type VenueResourceScopeQuery = z.infer<typeof venueResourceScopeQuerySchema>;

export const createVenueResourceRequestSchema = z
  .object({
    festivalId: z.uuid(),
    /** En type huset har. Utelatt: `newType`, som lages i samme kall. */
    resourceTypeId: z.uuid().optional(),
    newType: newVenueResourceTypeSchema.optional(),
    name: z.string().trim().min(1).max(120),
    subtitle: z.string().trim().max(160).nullish(),
    /** Antallet (B9): det føres her, på ressursen, ikke på typen. */
    capacityOverride: quantity.nullish(),
    notes: z.string().trim().max(2000).nullish(),
    groupId: z.uuid().nullish(),
    properties: z.array(propertyInput).max(20).optional(),
  })
  .refine((body) => (body.resourceTypeId === undefined) !== (body.newType === undefined), {
    message: 'Give a type or a new type, not both.',
    path: ['resourceTypeId'],
  })
  .meta({ id: 'CreateVenueResourceRequest' });
export type CreateVenueResourceRequest = z.infer<typeof createVenueResourceRequestSchema>;

export const updateVenueResourceRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    subtitle: z.string().trim().max(160).nullish(),
    capacityOverride: quantity.nullish(),
    notes: z.string().trim().max(2000).nullish(),
    archived: z.boolean().optional(),
    groupId: z.uuid().nullish(),
    /** Hele lista: egenskaper som ikke står her, tømmes på ressursen (feltet blir stående på typen). */
    properties: z.array(propertyInput).max(20).optional(),
  })
  .meta({ id: 'UpdateVenueResourceRequest' });
export type UpdateVenueResourceRequest = z.infer<typeof updateVenueResourceRequestSchema>;

/* ---------------------------------------------------------------------------- grupper (B9) */

/**
 * SPILLESTEDETS GRUPPER (1237). Det samme treet som avdelingene (`departments`), med spillestedets omfang og
 * uten årgang, i så mange nivåer man vil.
 */
export const venueGroupSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    parentId: z.uuid().nullable(),
    sortOrder: z.number().int(),
    /** Ressurser rett i gruppen (ikke undergruppene). */
    resourceCount: z.number().int(),
  })
  .meta({ id: 'VenueGroup' });
export type VenueGroup = z.infer<typeof venueGroupSchema>;

export const venueGroupListSchema = z
  .object({ items: z.array(venueGroupSchema) })
  .meta({ id: 'VenueGroupList' });
export type VenueGroupList = z.infer<typeof venueGroupListSchema>;

export const createVenueGroupRequestSchema = z
  .object({
    festivalId: z.uuid(),
    name: z.string().trim().min(1).max(80),
    parentId: z.uuid().nullish(),
  })
  .meta({ id: 'CreateVenueGroupRequest' });
export type CreateVenueGroupRequest = z.infer<typeof createVenueGroupRequestSchema>;

export const updateVenueGroupRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(80).optional(),
    parentId: z.uuid().nullish(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .meta({ id: 'UpdateVenueGroupRequest' });
export type UpdateVenueGroupRequest = z.infer<typeof updateVenueGroupRequestSchema>;
